import { useState } from 'react'
import Styles from '../Search.module.scss'
import { Configure, Index, connectStateResults } from 'react-instantsearch-dom'
import { ConnectedSearchResults as SearchResults } from '../SearchResults/SearchResults'
import { SearchConfigProps, SearchStateProps } from 'components/WorkspaceDashboardSearchBar/Types'
import { useSearchList } from './useSearchList'
import { useObjectRef } from 'utils/useObjectRef'
import { SearchResultType } from 'components/Search/constants'
import { useKeyboardNavigation } from 'hooks/useKeyboardNavigationHook'
import { Empty } from './Empty/Empty'
import { Loading } from './Loading/Loading'
import { Section } from 'screens/blaze/Home/components/Catalogue/components/Section/Section'
import { ViewDropdown } from './ViewDropdown'
import { FileTypeFilter } from './Filters/FileTypeFilter'
import { useSearch } from '../useSearch'
import { SetState } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { HeadingLevel } from 'blaze-ui/Heading/Heading'
import { LocationFilter } from './Filters/LocationFilter'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import cx from 'classnames'
import { LoadingRows } from 'screens/RecentDocsContainer/components/FilesTable/LoadingRows'

export type ViewType = 'Grid' | 'List'

interface Props {
  isSearchStalled: boolean
  allSearchResults: any
  loadMore: (type: SearchResultType) => void
  searchConfiguration: SearchConfigProps
  onReset: () => void
  query: string
  setFileTypeFilter: (type: string) => void
  setSearchConfiguration: SetState<SearchConfigProps>
  setSearchState: SetState<SearchStateProps>
}

export const SearchList = ({
  allSearchResults,
  isSearchStalled,
  onReset,
  searchConfiguration,
  query,
  setSearchConfiguration,
  setSearchState,
}: Props) => {
  const ref = useObjectRef<HTMLDivElement>()
  useKeyboardNavigation(ref)
  const { isTabletPortrait, isMobile } = useResponsiveDesign()
  const [view, setView] = useState<ViewType>(isMobile ? 'List' : 'Grid')
  const currentWorkspaceId = useCurrentWorkspaceId()

  const { setFileTypeFilter, filterFileType, setFolderFilter, filterFolder } = useSearch({
    setSearchConfiguration,
    setSearchState,
    workspaceId: currentWorkspaceId,
  })

  const { hasResults, loaded } = useSearchList({
    allSearchResults,
    searchConfiguration,
  })

  const actionRight = isTabletPortrait ? (
    <FileTypeFilter setFileTypeFilter={setFileTypeFilter} activeFilter={filterFileType} />
  ) : (
    <div className={Styles.filters}>
      <LocationFilter setFolderFilter={setFolderFilter} activeFilter={filterFolder} />
      <ViewDropdown viewType={view} onSelect={setView} />
    </div>
  )

  const sectionProps = {
    titleLevel: 4 as HeadingLevel,
    scrollable: false,
    actionRight: isMobile ? undefined : actionRight,
    actionLeft: isTabletPortrait ? undefined : (
      <FileTypeFilter setFileTypeFilter={setFileTypeFilter} activeFilter={filterFileType} />
    ),
  }

  const loadingComponent =
    view === 'List' ? (
      <div className={Styles.itemsList}>
        <LoadingRows loadingCellWidth={180} isInitialLoad />
      </div>
    ) : (
      <Loading />
    )

  if (!hasResults) {
    return (
      <div className={Styles.noResultContainer}>
        <Section {...sectionProps} size="xs">
          {isSearchStalled || !loaded ? <>{loadingComponent}</> : <Empty onReset={onReset} query={query} />}
          <Index
            indexName={searchConfiguration.indexName}
            key={searchConfiguration.name}
            indexId={searchConfiguration.indexId}
          >
            <Configure
              hitsPerPage={searchConfiguration.hitsPerPage}
              restrictSearchableAttributes={searchConfiguration.searchAttributes}
              filters={searchConfiguration.filters}
            />
          </Index>
        </Section>
      </div>
    )
  }

  return (
    <Section {...sectionProps} size="xs">
      {isTabletPortrait && !isMobile && (
        <div className={cx(Styles.filters, Styles.mobileFilters)}>
          <LocationFilter setFolderFilter={setFolderFilter} activeFilter={filterFolder} />
          <ViewDropdown viewType={view} onSelect={setView} />
        </div>
      )}
      <div ref={ref}>
        <Index
          indexName={searchConfiguration.indexName}
          key={searchConfiguration.name}
          indexId={searchConfiguration.indexId}
        >
          <Configure
            hitsPerPage={searchConfiguration.hitsPerPage}
            restrictSearchableAttributes={searchConfiguration.searchAttributes}
            filters={searchConfiguration.filters}
            attributesToSnippet={searchConfiguration.attributesToSnippet}
            snippetEllipsisText={searchConfiguration.snippetEllipsisText}
          />
          <SearchResults viewType={view} />
        </Index>
      </div>
    </Section>
  )
}

export const ConnectedSearchList: React.ComponentClass<any> = connectStateResults(SearchList)
